import { useEffect, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import U2000SaveCancelDialog from '../../u2000Core/components/dialog/U2000SaveCancelDialog';
import U2000InputText from '../../u2000Core/components/formControls/input/U2000InputText';
import u2000FormState, {
  U2000FormStateEnumEditMode,
} from '../../u2000Core/reducer/formReducer/U2000FormState';
import {
  u2000IntFormState,
  useU2000FormStateHandlers,
} from '../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { NS } from '../u340xKpiNs';
import { U340X_KPI_MIN_MONTHS } from '../types/u340xKpiTypes';
import { u340xIsValidMonths } from '../utils/u340xKpiMonths';

type MonthsFormRow = {
  months: string;
};

export interface U340xKpiMonthsConfigDialogProps {
  isOpen: boolean;
  months: number;
  maxMonths: number;
  minMonths?: number;
  onClose: () => void;
  onSave: (months: number) => void;
}

function U340xKpiMonthsConfigDialog({
  isOpen,
  months,
  maxMonths,
  minMonths = U340X_KPI_MIN_MONTHS,
  onClose,
  onSave,
}: U340xKpiMonthsConfigDialogProps) {
  const { t } = useTranslation(NS);
  const focusRef = useRef<HTMLInputElement>(null);
  const [formState, formStateDispatch] = useReducer(
    u2000FormState,
    u2000IntFormState,
  );

  const formStateHandlers = useU2000FormStateHandlers<MonthsFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: {
      newRecord: () => ({ months: String(months) }),
      validateForm: (row) => {
        const result: Record<string, string> = {};
        const parsed = Number(row.months);
        if (!u340xIsValidMonths(parsed, maxMonths, minMonths)) {
          result.months = t('config.monthsInvalid', { min: minMonths, max: maxMonths });
        }
        return result;
      },
      saveForme: async () => undefined,
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    formStateHandlers.setData({ months: String(months) });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset à l'ouverture uniquement
  }, [isOpen, months, maxMonths, minMonths]);

  const draftMonths = Number(formStateHandlers.getData()?.months);
  const draftValid = u340xIsValidMonths(draftMonths, maxMonths, minMonths);

  const handleClose = (action: 'save' | 'cancel') => {
    if (action === 'cancel') {
      onClose();
      return;
    }
    const parsed = Number(formStateHandlers.getData()?.months);
    if (!u340xIsValidMonths(parsed, maxMonths, minMonths)) {
      return;
    }
    onSave(parsed);
    onClose();
  };

  return (
    <U2000SaveCancelDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={t('config.title')}
      width="small"
      isValid={draftValid}
      defaultFocusRef={focusRef}
    >
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          {t('config.monthsHint', { min: minMonths, max: maxMonths })}
        </p>
        <U2000InputText
          formStateHandlers={formStateHandlers}
          fieldName="months"
          label={t('config.monthsLabel')}
          type="number"
          placeholder={String(months)}
          inputRef={focusRef as import('react').RefObject<HTMLInputElement>}
        />
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U340xKpiMonthsConfigDialog;
